import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ScheduleConfigModal = ({ isOpen, onClose, onSave, existingSchedule = null }) => {
  const [formData, setFormData] = useState({
    scheduleName: existingSchedule?.scheduleName || '',
    frequency: existingSchedule?.frequency || 'monthly',
    departments: existingSchedule?.departments || [],
    nextRunDate: existingSchedule?.nextRunDate || '',
    isActive: existingSchedule?.isActive ?? true,
    autoApprove: existingSchedule?.autoApprove ?? false
  });

  const departmentOptions = ['HR', 'Finance', 'Operations', 'IT', 'Sales', 'Marketing'];

  const handleDepartmentToggle = (dept) => {
    setFormData(prev => ({
      ...prev,
      departments: prev?.departments?.includes(dept)
        ? prev?.departments?.filter(d => d !== dept)
        : [...prev?.departments, dept]
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {existingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Schedule Name *
            </label>
            <Input
              value={formData?.scheduleName}
              onChange={(e) => setFormData({ ...formData, scheduleName: e?.target?.value })}
              placeholder="e.g., Monthly All Departments"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Frequency *
            </label>
            <Select
              value={formData?.frequency}
              onChange={(e) => setFormData({ ...formData, frequency: e?.target?.value })}
              required
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Next Run Date *
            </label>
            <Input
              type="date"
              value={formData?.nextRunDate}
              onChange={(e) => setFormData({ ...formData, nextRunDate: e?.target?.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Departments *
            </label>
            <div className="grid grid-cols-2 gap-3">
              {departmentOptions?.map((dept) => (
                <div key={dept} className="flex items-center gap-2">
                  <Checkbox
                    checked={formData?.departments?.includes(dept)}
                    onChange={() => handleDepartmentToggle(dept)}
                  />
                  <span className="text-sm text-foreground">{dept}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData?.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e?.target?.checked })}
              />
              <label className="text-sm text-foreground">Active Schedule</label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData?.autoApprove}
                onChange={(e) => setFormData({ ...formData, autoApprove: e?.target?.checked })}
              />
              <label className="text-sm text-foreground">Auto-approve payroll runs</label>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <Button type="submit" variant="primary" iconName="Save">
              {existingSchedule ? 'Update Schedule' : 'Create Schedule'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleConfigModal;